import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { roleQuestion, type RoleAnswers, type RoleTeam } from '../utils/teamRoles';

/**
 * "Role in <team>" groups inside the attributes dropdown, directly under the
 * Teams list: one per team the member picked that has roles, each offering
 * only that team's roles (one of them) or "My role isn't listed" (the member
 * carries on without a role, is flagged as having none, and their team's
 * managers are told; they are asked again once a new role is set up). Drawn to
 * match the dropdown it sits in: the attributes editor ('modal') or the
 * onboarding step ('onboarding').
 */
const TeamRoleGroups: React.FC<{
  teams: RoleTeam[];
  answers: RoleAnswers;
  /** A role id, null for "My role isn't listed", undefined to take the answer back. */
  onAnswer: (teamValueId: string, roleId: string | null | undefined) => void;
  look: 'modal' | 'onboarding';
}> = ({ teams, answers, onAnswer, look }) => {
  const onboarding = look === 'onboarding';

  const box = (selected: boolean) =>
    onboarding ? (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '3px',
          border: '2px solid',
          borderColor: selected ? '#F79420' : '#565656',
          bgcolor: selected ? '#F79420' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        {selected && <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />}
      </Box>
    ) : (
      <Box
        sx={{
          width: 20,
          height: 20,
          p: '3px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box'
        }}
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '4px',
            bgcolor: 'var(--Fill-Transparent-trans-normal-9/90, rgba(255,255,255,0.09))',
            border: '1px solid',
            borderColor: selected
              ? 'var(--Stroke-Form-Input-border-highlight, rgba(255,255,255,0.9))'
              : 'var(--Stroke-Form-Input-border-light, rgba(134,134,134,0.4))',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {selected && <CheckIcon sx={{ fontSize: 12, color: 'var(--Stroke-Form-Input-border-highlight, #fff)' }} />}
        </Box>
      </Box>
    );

  const row = {
    width: '100%',
    px: 2,
    py: onboarding ? 0.75 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: onboarding ? 1.5 : 2,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left' as const,
    '&:hover': { bgcolor: onboarding ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.08)' }
  };

  return (
    <>
      {teams.map((team) => {
        const answer = answers[team.teamValueId];
        return (
          <Box key={`role:${team.teamValueId}`} data-testid="team-role-group" data-team-id={team.teamValueId}>
            <Box sx={{ px: 2, pt: onboarding ? 1 : '5px', pb: 0.25, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={
                  onboarding
                    ? {
                        color: '#868686',
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }
                    : {
                        color: 'var(--Text-text-tertiary, rgba(255,255,255,0.55))',
                        fontSize: 12,
                        fontFamily: 'Inter',
                        lineHeight: '16px'
                      }
                }
              >
                {roleQuestion(team)}
              </Typography>
              {answer === undefined && (
                <Typography
                  component="span"
                  sx={{
                    color: onboarding ? '#F79420' : 'var(--Text-text-warning, #f79420)',
                    fontSize: onboarding ? 11 : 12,
                    fontWeight: 500
                  }}
                >
                  Needs an answer
                </Typography>
              )}
            </Box>
            {team.roles.map((role) => {
              const selected = answer === role.id;
              return (
                <Box
                  key={role.id}
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={`${roleQuestion(team)}: ${role.name}`}
                  onClick={() => onAnswer(team.teamValueId, selected ? undefined : role.id)}
                  sx={row}
                >
                  {box(selected)}
                  <Typography
                    sx={
                      onboarding
                        ? { fontSize: 14, color: selected ? '#FDFDFD' : '#B6B6B6' }
                        : {
                            color: selected
                              ? 'var(--Text-text-primary, #fdfdfd)'
                              : 'var(--Text-text-secondary, rgba(255,255,255,0.85))',
                            fontSize: 14,
                            fontFamily: 'Inter',
                            lineHeight: '20px'
                          }
                    }
                  >
                    {role.name}
                  </Typography>
                </Box>
              );
            })}
            <Box
              component="button"
              type="button"
              role="radio"
              aria-checked={answer === null}
              aria-label={`${roleQuestion(team)}: My role isn't listed`}
              onClick={() => onAnswer(team.teamValueId, answer === null ? undefined : null)}
              sx={row}
            >
              {box(answer === null)}
              <Typography
                sx={
                  onboarding
                    ? { fontSize: 14, color: '#B6B6B6', fontStyle: 'italic' }
                    : {
                        color:
                          answer === null
                            ? 'var(--Text-text-primary, #fdfdfd)'
                            : 'var(--Text-text-tertiary, rgba(255,255,255,0.55))',
                        fontSize: 14,
                        fontFamily: 'Inter',
                        fontStyle: 'italic',
                        lineHeight: '20px'
                      }
                }
              >
                My role isn&apos;t listed
              </Typography>
            </Box>
          </Box>
        );
      })}
    </>
  );
};

export default TeamRoleGroups;
